import { type Dict } from "mixpanel-browser";
import { type OmitFromUnion } from "types/utility-types";
import { store } from "store";
import { calculateAge } from "utils/birthdate";
import { countNonAdultTravelers } from "utils/traveler";
import { gtmTrack } from "./gtm";
import { mixpanelTrack } from "./mixpanel";

interface ErrorEventProperties {
  error_message: string;
  error_code: string;
  component_type: string;
}

interface ComponentEventProperties {
  component_name: string;
  component_type: string;
  component_region: string;
}

interface InteractionEventProperties {
  interaction_text?: string;
  interaction_region: string;
  component_name?: string;
  interaction_type:
    | "button"
    | "checkbox"
    | "radio"
    | "floating"
    | "file"
    | "text"
    | "textarea"
    | "drop down"
    | "combo box"
    | "component"
    | "link"
    | "banner"
    | "icon";
}

interface EventPropertyMapping {
  "page view": never;
  business: never;
  component: ComponentEventProperties;
  error: ErrorEventProperties;
  interaction: InteractionEventProperties;
}

type EventType = keyof EventPropertyMapping;

type EventProperties = {
  [K in keyof EventPropertyMapping]: {
    event_type: K;
    name_of_page: string;
  } & EventPropertyMapping[K] &
    Dict;
}[keyof EventPropertyMapping];

function trackEventFactory<T extends EventType>(eventType: T) {
  return (
    eventName: string,
    properties: OmitFromUnion<
      Extract<EventProperties, { event_type: T }>,
      "event_type"
    >,
  ) => {
    mixpanelTrack(eventName, {
      event_type: eventType,
      ...properties,
    });
  };
}

export const test: Omit<
  {
    event_type: "interaction";
    name_of_page: string;
  } & Dict &
    InteractionEventProperties,
  "event_type"
> = {
  interaction_type: "bump",
};

export const trackInteraction = trackEventFactory("interaction");
export const trackError = trackEventFactory("error");
export const trackComponent = trackEventFactory("component");
export const trackBusiness = trackEventFactory("business");

export function trackPageView(
  pageName: string,
  pathname: string,
  mixpanelProperties?: Dict,
) {
  const { quote } = store.getState();

  gtmTrack("page_view", {
    page_location: pathname,
    page_title: pageName,
  });

  mixpanelTrack("page_view", {
    $first_name: quote.name?.firstName,
    $last_name: quote.name?.lastName,
    main_traveler_age:
      quote.travelers?.[0] && calculateAge(quote.travelers[0].dateOfBirth),
    all_travelers_ages:
      quote.travelers?.map(({ dateOfBirth }) => calculateAge(dateOfBirth)) ??
      [],
    name_of_page: pageName,
    non_adult_travelers_count: countNonAdultTravelers(quote.travelers),
    total_travelers_count: quote.travelers?.length,
    ...mixpanelProperties,
  });
}
